import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import CalendarShell from '../calendar/CalendarShell';
import { getTimeFormat } from 'common/dateFormatConfig';
import { useV6TimeEvents } from './hooks/useV6TimeEvents';
import useScheduleDayEventsLoad from '../hooks/useScheduleDayEventsLoad';
import useV6TimeEventColumns from './hooks/useV6TimeEventColumns';
import { TIME_VIEW_COLUMN_WIDTH } from 'pages/ScheduleV6/constants/constants';
import EventCard from '../events/EventCard';
import { getSheetStatus } from '../events/helpers';

/**
 * Daily (time grid) view.
 *
 * Mapping and heavy logic must live in hooks.
 */
const DailyView = ({
  calendarRef,
  selectedDate,
  dateFormat,
  timeFormat,
  noClSheets,
  hasEditAccess,
}) => {
  useScheduleDayEventsLoad(selectedDate);
  const events = useV6TimeEvents();
  const { sheetColumnMap } = useV6TimeEventColumns(events);

  const eventsWithColumnId = useMemo(() => {
    return events.map(event => ({
      ...event,
      id: `${event.extendedProps?.sheet?._id}_` +
        `col-${sheetColumnMap[event.extendedProps?.sheet?._id]}_` + 
        `w-${event.extendedProps?.sheet?.workers?.length}_` + 
        `e-${event.extendedProps?.sheet?.equipment?.length}_` +
        `status-${getSheetStatus(event.extendedProps?.sheet)?.status}_` +
        `notes-${event.extendedProps?.sheet?.notes}`
    }));
  }, [events, sheetColumnMap])

  const timeSettings = useMemo(
    () => getTimeFormat([], timeFormat, dateFormat),
    [timeFormat, dateFormat]
  );

  const slotLabelFormat = useMemo(
    () =>
      timeSettings.is12Format
        ? undefined
        : {
            hour: '2-digit',
            minute: '2-digit',
          },
    [timeSettings.is12Format]
  );


  useEffect(() => {
    const maxColumn = Math.max(...(Object.values(sheetColumnMap)));

    if (calendarRef?.current?.elRef) {
      calendarRef.current.elRef.current.style.setProperty('min-width', `${(maxColumn + 2) * 202}px`);
      calendarRef.current?.getApi?.()?.updateSize?.();
    }
  }, [calendarRef, sheetColumnMap]);

  const renderEventContent = useCallback(
    (arg) => (
      <EventCard
        event={arg.event}
        dateFormat={dateFormat}
        timeFormat={timeFormat}
        noClSheets={noClSheets}
        hasEditAccess={hasEditAccess}
      />
    ),
    [dateFormat, timeFormat, noClSheets, hasEditAccess]
  );

  const eventClassNames = () => {
    return ['fc-event-width'];
  }

  const eventDidMount = (info) => {
    info.el.style.width = `200px`;

    const col = sheetColumnMap[info.event.extendedProps?.sheet?._id];
    info.el.style.left = `${col * TIME_VIEW_COLUMN_WIDTH}px`;

    const harness = info.el.parentElement;
    harness.style.right = "auto";
  };

  return (
    <CalendarShell
      calendarRef={calendarRef}
      initialView="timeGridDay"
      initialDate={selectedDate}
      events={eventsWithColumnId}
      renderEventContent={renderEventContent}
      minTime="00:00:00"
      maxTime="24:00:00"
      locale={timeSettings.is12Format ? '' : 'en-GB'}
      nowIndicator
      slotLabelFormat={slotLabelFormat}
      eventClassNames={eventClassNames}
      eventDidMount={eventDidMount}
      eventOverlap={true}
      slotEventOverlap={true}
    />
  );
};

DailyView.propTypes = {
  calendarRef: PropTypes.object,
  selectedDate: PropTypes.instanceOf(Date),
  dateFormat: PropTypes.string,
  timeFormat: PropTypes.string,
  noClSheets: PropTypes.arrayOf(PropTypes.string),
  hasEditAccess: PropTypes.bool,
};

export default DailyView;

